import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { api, setAuthToken, getErrorInfo } from '../services/api';
import { User, LoginCredentials } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<void>;
  isAuthenticated: boolean;
  /** Backend AUTH_1006 message when access is blocked outside working hours. */
  boundaryMessage: string | null;
  clearBoundaryMessage: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [boundaryMessage, setBoundaryMessage] = useState<string | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const scheduleTokenRefresh = () => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, 25 * 60 * 1000);
  };

  // Outside working hours (AUTH_1006) the refresh cookie stays valid; retry
  // periodically so the app signs back in automatically once hours reopen
  // (e.g. after an overtime grant).
  const scheduleBoundaryRetry = () => {
    clearRefreshTimer();
    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, 10 * 60 * 1000);
  };

  const login = async (credentials: LoginCredentials) => {
    try {
      const response = await api.post('/auth/login', {
        identifier: credentials.identifier,
        password: credentials.password,
        device_id: getDeviceId(),
      });

      const { access_token, user } = response.data;
      
      setAuthToken(access_token);
      localStorage.setItem('access_token', access_token);
      setUser(user);
      setBoundaryMessage(null);
      scheduleTokenRefresh();
    } catch (error: any) {
      const info = getErrorInfo(error);
      if (info.code === 'AUTH_1006') {
        setBoundaryMessage(info.message || 'Outside working hours');
      }
      const err: any = new Error(info.message || 'Login failed');
      err.code = info.code;
      throw err;
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setAuthToken(null);
      localStorage.removeItem('access_token');
      setUser(null);
      clearRefreshTimer();
    }
  };

  const refreshToken = async () => {
    try {
      const response = await api.post('/auth/refresh');
      const { access_token, user } = response.data;
      
      setAuthToken(access_token);
      localStorage.setItem('access_token', access_token);
      setUser(user);
      setBoundaryMessage(null);
      scheduleTokenRefresh();
    } catch (error: any) {
      console.error('Token refresh failed:', error);
      const info = getErrorInfo(error);
      if (info.code === 'AUTH_1006') {
        // Outside working hours: do NOT call the logout endpoint (that would clear
        // the refresh cookie). Only drop the in-memory auth state so the app can
        // auto-login at the next in-hours refresh.
        setBoundaryMessage(info.message || 'Outside working hours');
        setAuthToken(null);
        setUser(null);
        scheduleBoundaryRetry();
        return;
      }
      await logout();
    }
  };

  const getDeviceId = (): string => {
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = generateDeviceId();
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  };

  const generateDeviceId = (): string => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('access_token');
      if (token) {
        setAuthToken(token);
        try {
          const response = await api.get('/me');
          setUser(response.data);
          scheduleTokenRefresh();
        } catch (error: any) {
          console.error('Failed to get user info:', error);
          const info = getErrorInfo(error);
          if (info.code === 'AUTH_1006') {
            // Blocked outside working hours — keep credentials for auto-recovery.
            setBoundaryMessage(info.message || 'Outside working hours');
            setAuthToken(null);
            scheduleBoundaryRetry();
          } else {
            await logout();
          }
        }
      }
      setLoading(false);
    };

    initAuth();

    return () => {
      clearRefreshTimer();
    };
  }, []);

  const value = {
    user,
    loading,
    login,
    logout,
    refreshToken,
    isAuthenticated: !!user,
    boundaryMessage,
    clearBoundaryMessage: () => setBoundaryMessage(null),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
